import { ArrowRight } from 'lucide-react'

import applicationImage from '@/assets/application.png'
import quotesImage from '@/assets/quotes.png'
import { Anchor } from '@/components/ui/Anchor'
import { Banner } from '@/components/ui/banner'
import { Button } from '@/components/ui/button'
import { cn } from '@/lib/utils'
import { OnboardingStage } from '@/models/user'

interface OnboardingBannerProps {
  /** Which of the three points in the journey the org is at. */
  stage: OnboardingStage
}

// 90° minus the 34° lean measured off the illustrations' parallelogram edges, which
// slant their uprights but keep their top and bottom edges level.
const GRID_TILT = 56

// Faded out well before the right edge so it never sits under the illustrations.
const GRID_FADE = 'linear-gradient(to right, #000 0%, #000 30%, transparent 60%)'

// currentColor picks up the banner tone's ink, so the grid tints with the variant.
const GRID_TEXTURE: React.CSSProperties = {
  backgroundImage: `repeating-linear-gradient(${GRID_TILT}deg, currentColor 0 1px, transparent 1px 16px), repeating-linear-gradient(180deg, currentColor 0 1px, transparent 1px 16px)`,
  maskImage: GRID_FADE,
  WebkitMaskImage: GRID_FADE,
}

interface StageCopy {
  title: React.ReactNode
  body: React.ReactNode
  cta: string
  /** The chat until carriers have been asked for prices, the quotes page after. */
  href: string
  /** Nothing is being asked of the customer, so the CTA steps back to outline. */
  passive: boolean
  /** Greys the banner out — the journey is paused on someone other than the customer. */
  muted?: boolean
  image?: string
}

const COPY: Record<OnboardingStage, StageCopy> = {
  [OnboardingStage.APPLICATION_INCOMPLETE]: {
    title: 'Continue your insurance application',
    body: (
      <>
        You’re minutes away from getting quotes{' '}
        {/* Desktop only — at phone width the copy already wraps here on its own. */}
        <br className="max-sm:hidden" />
        from top insurance providers.
      </>
    ),
    cta: 'Finish application',
    href: '/application',
    passive: false,
    image: applicationImage,
  },
  [OnboardingStage.QUOTES_READY]: {
    title: 'Review your quotes',
    body: (
      <>
        Compare offers on your quotes page, <br className="max-sm:hidden" />
        and choose coverages that are right for your fleet.
      </>
    ),
    cta: 'Go to quotes',
    href: '/quotes',
    passive: false,
    image: quotesImage,
  },
  [OnboardingStage.WITH_BROKER]: {
    title: (
      <>
        Your broker is finalizing your <br className="max-sm:hidden" />
        requested coverage
      </>
    ),
    // No deadline: this runs until the policy binds, so a 24-hour promise would go stale.
    body: 'They’ll be in touch with next steps once underwriting is complete.',
    cta: 'View your quotes',
    href: '/quotes',
    passive: true,
    muted: true,
  },
}

export function OnboardingBanner({ stage }: OnboardingBannerProps) {
  const copy = COPY[stage]

  return (
    <Banner
      variant="info"
      className={cn(
        'relative grid min-h-[200px] items-center overflow-hidden p-8',
        copy.muted && 'border-ink-200 bg-ink-100 text-ink-700'
      )}
      icon={null}
      action={
        // relative lifts the CTA over the artwork — both positioned, so DOM order wins.
        <Button
          href={copy.href}
          variant={copy.passive ? 'outline' : 'default'}
          className="relative max-sm:w-full"
        >
          {copy.cta}
          {!copy.passive && <ArrowRight />}
        </Button>
      }
    >
      {copy.image && (
        <img src={copy.image} alt="" className="absolute top-0 right-0 h-full w-auto max-sm:hidden" />
      )}
      <div aria-hidden className="pointer-events-none absolute inset-0 opacity-5" style={GRID_TEXTURE} />
      <div className="relative max-w-lg min-w-0">
        <strong className="block text-2xl font-bold tracking-[-0.01em]">{copy.title}</strong>
        <p className="text-md mt-2 leading-relaxed">{copy.body}</p>
      </div>
      {/* Last child, so it covers the copy; the CTA renders after it and stays on top. */}
      <Anchor href={copy.href} aria-hidden tabIndex={-1} className="absolute inset-0" />
    </Banner>
  )
}
